const REDUCED_MOTION_MQ = '(prefers-reduced-motion: reduce)';

/** Найти ближайший скролл-контейнер от элемента (overflow: scroll/auto). */
export function getScrollParent(el: Element | null): HTMLElement | null {
  if (!el) return null;
  let p = el.parentElement;
  while (p) {
    const s = getComputedStyle(p);
    const oy = s.overflowY;
    const o = s.overflow;
    if (oy === 'scroll' || oy === 'auto' || o === 'scroll' || o === 'auto') return p;
    p = p.parentElement;
  }
  return null;
}

/** Порог «приклеен к низу» для мессенджера (px). */
export const MESSENGER_AT_BOTTOM_THRESHOLD_PX = 80;

/** Нижний край ленты чуть выше низа viewport — это недокрут, не «у низа» (после попапа / разделителя). */
const MESSENGER_BOTTOM_ALIGN_TOP_EPSILON_PX = 6;

export function messengerAllowsSmoothScroll(): boolean {
  return typeof window !== 'undefined' && !window.matchMedia(REDUCED_MOTION_MQ).matches;
}

export function viewportMaxScrollTop(viewport: HTMLElement): number {
  return Math.max(0, viewport.scrollHeight - viewport.clientHeight);
}

/** Не даём scrollTop выйти за [0, max] после смены scrollHeight (resize / подгрузка контента). */
export function clampViewportScrollTop(viewport: HTMLElement): void {
  const max = viewportMaxScrollTop(viewport);
  if (viewport.scrollTop > max) viewport.scrollTop = max;
  else if (viewport.scrollTop < 0) viewport.scrollTop = 0;
}

/**
 * Реальный низ: scrollHeight − clientHeight иногда «не хватает» на 1px (DPR/OS);
 * присвоение scrollTop = scrollHeight даёт clamp до истинного максимума.
 */
function snapMessengerViewportToBottom(viewport: HTMLElement): void {
  viewport.scrollTop = viewport.scrollHeight;
}

export function isMessengerViewportAtBottom(
  viewport: HTMLElement | null,
  thresholdPx: number = MESSENGER_AT_BOTTOM_THRESHOLD_PX
): boolean {
  if (!viewport) return false;
  const { scrollTop, scrollHeight, clientHeight } = viewport;
  return scrollHeight - scrollTop - clientHeight <= thresholdPx;
}

/**
 * Низ ленты: по возможности по нижнему краю корня ленты (.messengerChat__messagesInner) —
 * тогда учитываются все слоты, в т.ч. разделитель «Непрочитанные» над баблом.
 * Иначе — нижний край последнего узла (хвост) или scrollTop ≈ max.
 */
export function isMessengerTailStuckToVisualBottom(
  viewport: HTMLElement | null,
  tailEl: Element | null,
  thresholdPx: number = MESSENGER_AT_BOTTOM_THRESHOLD_PX,
  scrollContentRoot?: Element | null
): boolean {
  if (viewport && scrollContentRoot && viewport.contains(scrollContentRoot)) {
    const maxTop = viewportMaxScrollTop(viewport);
    if (viewport.scrollTop < maxTop - thresholdPx) return false;

    const vr = viewport.getBoundingClientRect();
    const cr = scrollContentRoot.getBoundingClientRect();
    const gap = vr.bottom - cr.bottom;
    // gap > 0 ⇒ низ .messagesInner выше низа viewport — не докрутили (раньше давало ложное true через Math.abs).
    if (gap > MESSENGER_BOTTOM_ALIGN_TOP_EPSILON_PX) return false;
    return gap >= -thresholdPx;
  }
  if (!viewport || !tailEl || !viewport.contains(tailEl)) {
    return isMessengerViewportAtBottom(viewport, thresholdPx);
  }
  const maxTop = viewportMaxScrollTop(viewport);
  if (viewport.scrollTop < maxTop - thresholdPx) return false;

  const vr = viewport.getBoundingClientRect();
  const tr = tailEl.getBoundingClientRect();
  const gap = vr.bottom - tr.bottom;
  if (gap > MESSENGER_BOTTOM_ALIGN_TOP_EPSILON_PX) return false;
  return gap >= -thresholdPx;
}

/** Совпадает по порядку величины с CSS messengerMessageEnter (~0.5s ease-out). */
const DM_SCROLL_ANIM_MS = 500;
let dmTailScrollAnimGeneration = 0;

/** Прервать текущую rAF-анимацию к хвосту (перед единым прыжком FAB). */
export function cancelMessengerTailScrollAnimation(): void {
  dmTailScrollAnimGeneration += 1;
}

/** Плавный докрут к актуальному max scrollTop; maxTop пересчитывается каждый кадр (рост ленты во время анимации). */
function animateViewportScrollTopToMax(viewport: HTMLElement, durationMs: number): void {
  const gen = ++dmTailScrollAnimGeneration;
  const startTop = viewport.scrollTop;
  const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const step = (now: number) => {
    if (gen !== dmTailScrollAnimGeneration) return;
    const maxTop = viewportMaxScrollTop(viewport);
    const t = Math.min(1, (now - t0) / durationMs);
    const eased = 1 - (1 - t) ** 3;
    const nextTop = startTop + (maxTop - startTop) * eased;
    viewport.scrollTop = Math.max(0, Math.min(nextTop, maxTop));
    if (t < 1) {
      requestAnimationFrame(step);
    } else {
      snapMessengerViewportToBottom(viewport);
      requestAnimationFrame(() => snapMessengerViewportToBottom(viewport));
    }
  };
  requestAnimationFrame(step);
}

/**
 * Единый скролл к хвосту ленты (новые сообщения): низ = scrollTop max.
 *
 * NOTE: scrollIntoView/scrollTo с behavior:'smooth' не работает плавно внутри OverlayScrollbars
 * (скролл происходит мгновенно). Поэтому в smooth-режиме всегда используем rAF-анимацию,
 * а tailEl задействуется только для мгновенного скролла.
 *
 * @param opts.deferSecondSnap — по умолчанию true: повторный snap на следующем кадре (OverlayScrollbars).
 *   false — один snap сразу; используйте при вызове из useLayoutEffect, чтобы не было второго кадра.
 */
export function scrollMessengerTailIntoView(
  viewport: HTMLElement | null,
  tailEl: Element | null,
  mode: 'smooth' | 'instant',
  opts?: { deferSecondSnap?: boolean }
): void {
  const deferSecondSnap = opts?.deferSecondSnap !== false;
  if (mode === 'instant' || !messengerAllowsSmoothScroll()) {
    dmTailScrollAnimGeneration += 1;
    if (tailEl) {
      tailEl.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
    if (viewport) {
      snapMessengerViewportToBottom(viewport);
      if (deferSecondSnap) {
        requestAnimationFrame(() => snapMessengerViewportToBottom(viewport));
      }
    }
    return;
  }
  if (!viewport) return;
  animateViewportScrollTopToMax(viewport, DM_SCROLL_ANIM_MS);
}

/** Снимок длины ленты для отличия prepend (loadOlder / trim) от реального append в хвост. */
export type MessengerMessageListSnap = {
  len: number;
  firstId: string | undefined;
  lastId: string | undefined;
};

/**
 * Сколько сообщений добавилось в хвост (не подгрузка старых сверху).
 * Если сменился firstId — это prepend/сдвиг окна (bidirectional window), не tail append.
 */
export function messengerTailAppendCount(
  prev: MessengerMessageListSnap,
  next: MessengerMessageListSnap
): number {
  if (next.len <= prev.len) return 0;
  if (next.lastId === prev.lastId) return 0;
  if (prev.firstId !== undefined && next.firstId !== prev.firstId) return 0;
  if (prev.len === 0 && prev.lastId === undefined) return 0;
  return next.len - prev.len;
}

/** Мгновенно к низу — подгрузка истории, откат позиции */
export function scrollMessengerViewportInstant(viewport: HTMLElement | null): void {
  if (!viewport) return;
  snapMessengerViewportToBottom(viewport);
  requestAnimationFrame(() => snapMessengerViewportToBottom(viewport));
}

/**
 * Плавный скролл к низу через rAF-анимацию.
 * Нативный scrollTo({behavior:'smooth'}) с OverlayScrollbars работает мгновенно.
 */
export function scrollMessengerViewportSmooth(viewport: HTMLElement | null): void {
  if (!viewport) return;
  if (!messengerAllowsSmoothScroll()) {
    snapMessengerViewportToBottom(viewport);
    requestAnimationFrame(() => snapMessengerViewportToBottom(viewport));
    return;
  }
  animateViewportScrollTopToMax(viewport, DM_SCROLL_ANIM_MS);
}

/** Хранилище для троттлинга плавного скролла (стрим, частые обновления). */
export type MessengerSmoothThrottleBucket = { lastAt: number };

export function scrollMessengerViewportSmoothThrottled(
  viewport: HTMLElement | null,
  bucket: MessengerSmoothThrottleBucket,
  minIntervalMs: number
): void {
  if (!viewport) return;
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  if (now - bucket.lastAt < minIntervalMs) return;
  bucket.lastAt = now;
  scrollMessengerViewportSmooth(viewport);
}

export function shiftViewportScrollTop(viewport: HTMLElement, delta: number): void {
  viewport.scrollTop += delta;
}
